"use client";

import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { cn } from "@/app/_module/lib/utils";

export interface BotIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface BotIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const BotIcon = forwardRef<BotIconHandle, BotIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 8V4H8" />
          <rect height="12" rx="2" width="16" x="4" y="8" />
          <path d="M2 14h2" />
          <path d="M20 14h2" />

          <motion.line
            animate={controls}
            initial="normal"
            variants={{
              normal: { y1: 13, y2: 15 },
              animate: {
                y1: [13, 14, 13],
                y2: [15, 14, 15],
                transition: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: 0.2,
                },
              },
            }}
            x1={15}
            x2={15}
          />

          <motion.line
            animate={controls}
            initial="normal"
            variants={{
              normal: { y1: 13, y2: 15 },
              animate: {
                y1: [13, 14, 13],
                y2: [15, 14, 15],
                transition: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: 0.2,
                },
              },
            }}
            x1={9}
            x2={9}
          />
        </svg>
      </div>
    );
  }
);

BotIcon.displayName = "Bot";

export { BotIcon };


/**
 * Auto-animating variant for touch surfaces. The base BotIcon blinks on
 * mouse-enter — which doesn't exist on a phone — so this one blinks itself:
 * every `blinkEvery` ms at rest, rapidly while `active` (e.g. while the bot
 * is thinking). Same eyes, no pointer required.
 */
export function AnimatedBotIcon({
  size = 24,
  className,
  active = false,
  blinkEvery = 4500,
}: {
  size?: number;
  className?: string;
  active?: boolean;
  blinkEvery?: number;
}) {
  const ref = useRef<BotIconHandle | null>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks -- stable component, hooks unconditional
  useEffect(() => {
    const blink = () => {
      ref.current?.startAnimation();
      // the "animate" variant self-resolves in ~700ms; return to normal after
      window.setTimeout(() => ref.current?.stopAnimation(), 750);
    };
    blink(); // one blink on mount — the icon announces it's alive
    const id = window.setInterval(blink, active ? 1100 : blinkEvery);
    return () => window.clearInterval(id);
  }, [active, blinkEvery]);

  return <BotIcon ref={ref} size={size} className={className} />;
}
